/**
 * QUOTATION LIST — elenco dei preventivi, paginato e ordinato server-side.
 *
 * Le colonne core sono sei più le azioni; i plugin abilitati ne aggiungono
 * altre contribuendo in slot `list:column` (vedi @crm/contracts/SlotContributor).
 * Ogni cella contribuita riceve `entityId` (l'id della riga) e `siblingIds`
 * (gli id di TUTTA la pagina): il plugin fa una sola chiamata batch al primo
 * mount invece di una per riga.
 */

import { useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { type GridColDef } from '@mui/x-data-grid';
import { useServerSearch, useServerListParams, useListDeleteHandler, formatDate, formatCurrency } from '@crm/shared';
import { ServerDataGrid } from '../../components/ServerDataGrid';
import type { SlotContributor } from '@crm/contracts';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useConfirmDialog } from '../../components/useConfirmDialog';
import { SearchGlyph, PlusGlyph, EditGlyph, TrashGlyph } from '../../components/icons';
import { useSlotContributors } from '../../plugin/useSlotContributors';
import { vocabularyLabelKey } from '../../plugin/vocabularyRegistry';
import { fetchBusinesses } from '../business/businessThunks';
import type { BusinessState } from '../business/businessTypes';
import { fetchQuotations, deleteQuotation } from './quotationThunks';
import { quotationStatusChipClass } from './quotationTypes';
import type { QuotationState } from './quotationTypes';

export default function QuotationList() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const confirm = useConfirmDialog();
  const { items, status, error, totalCount } = useAppSelector(
    (state) => state.quotations as QuotationState,
  );
  // Join client-side con businesses per risolvere il nome cliente: il DTO REST
  // espone `businessId` ma non `businessName` (vedi quotationMappers).
  const { items: businesses } = useAppSelector((state) => state.businesses as BusinessState);
  const businessById = useMemo(
    () => new Map(businesses.map((b) => [b.businessId, b.name])),
    [businesses],
  );
  const columnContributors = useSlotContributors('Quotation', 'list:column');

  // Ricerca server-side condivisa (min 3 caratteri + debounce 300ms).
  const { search, setSearch, query } = useServerSearch();
  const {
    paginationModel, setPaginationModel,
    sortModel, setSortModel,
    resetPage,
    backendParams,
  } = useServerListParams();

  // Reset a pagina 1 quando cambia il filtro: altrimenti si resterebbe su una
  // pagina che potrebbe essere oltre il nuovo totalCount.
  useEffect(() => { resetPage(); }, [query, resetPage]);

  useEffect(() => {
    dispatch(fetchQuotations({ ...backendParams, search: query || undefined }));
  }, [backendParams, query, dispatch]);

  useEffect(() => {
    if (businesses.length === 0) dispatch(fetchBusinesses());
  }, [businesses.length, dispatch]);

  // Delete + refetch coerente con totalCount/paginazione (vedi TS-9).
  const handleDelete = useListDeleteHandler({
    deleteThunk: deleteQuotation,
    fetchThunk: fetchQuotations,
    fetchParams: { ...backendParams, search: query || undefined },
    itemsLength: items.length,
    page: paginationModel.page,
    resetPage,
    dispatch,
  });

  // Id dell'intera pagina, passati a ogni cella contribuita.
  const pageIds = useMemo(() => items.map((q) => q.quotationId), [items]);

  const columns = useMemo(
    () => quotationColumns(confirm, handleDelete, t, businessById, columnContributors, pageIds),
    [confirm, handleDelete, t, businessById, columnContributors, pageIds],
  );

  if (status === 'failed') return <p className="text-error">{t('common.error', { message: error })}</p>;

  return (
    <div>
      {/* Testata come la lista Contatti, che e' la pagina di riferimento del
          nuovo design: titolo e conteggio a sinistra, ricerca e azioni a
          destra, il tutto fuori dalla card. */}
      <div className="page-head">
        <div>
          <h1 className="page-title">{t('quotation.title')}</h1>
          <span className="page-sub">{t('common.record', { count: totalCount })}</span>
        </div>
        <div className="page-actions">
          <div className="searchline">
            <SearchGlyph />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('quotation.searchPlaceholder')}
              aria-label={t('quotation.searchPlaceholder')}
            />
          </div>
          <Link to="/quotations/new" className="btn-primary">
            <PlusGlyph /> {t('quotation.newQuotation')}
          </Link>
        </div>
      </div>

      <div className="list-card">
        {status === 'loading' && <p className="list-feedback">{t('common.loading')}</p>}
        {status === 'succeeded' && items.length === 0 && (
          <p className="text-muted list-feedback">{t('quotation.noQuotations')}</p>
        )}
        {items.length > 0 && (
          <ServerDataGrid
            rows={items}
            columns={columns}
            getRowId={(row) => row.quotationId}
            rowCount={totalCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            sortModel={sortModel}
            onSortModelChange={setSortModel}
            loading={status === 'loading'}
            getRowHeight={() => 64}
            className="tk-grid"
          />
        )}
      </div>

      <ConfirmDialog open={confirm.isOpen} message={confirm.message} onConfirm={confirm.confirm} onCancel={confirm.cancel} />
    </div>
  );
}

function quotationColumns(
  confirm: ReturnType<typeof useConfirmDialog>,
  handleDelete: (quotationId: number) => void,
  t: (key: string, opts?: Record<string, unknown>) => string,
  businessById: Map<number, string>,
  contributors: SlotContributor[],
  pageIds: number[],
): GridColDef[] {
  return [
    {
      field: 'externalCode', headerName: t('quotation.columns.externalCode'), width: 140,
      renderCell: (params) => (
        <Link to={`/quotations/${params.row.quotationId}`} className="lc-code">{params.value || '—'}</Link>
      ),
    },
    {
      field: 'title', headerName: t('quotation.columns.title'), flex: 1,
      renderCell: (params) => (
        <span className="lc-name">
          <Link to={`/quotations/${params.row.quotationId}`} className="nm">{params.value}</Link>
        </span>
      ),
    },
    {
      // Colonna derivata da una lookup client-side: il backend NON accetta
      // `sort=businessName` perché il campo non esiste su Quotation. Senza
      // `sortable: false` un click sull'intestazione produrrebbe un 400.
      field: 'businessName', headerName: t('quotation.columns.client'), width: 180, sortable: false,
      valueGetter: (_value: unknown, row: { businessName?: string; businessId: number }) =>
        businessById.get(row.businessId) ?? row.businessName ?? `#${row.businessId}`,
    },
    {
      field: 'dueDate', headerName: t('quotation.columns.dueDate'), width: 130,
      renderCell: (params) => (
        <span className="cell-mono">{params.value ? formatDate(params.value as string) : '—'}</span>
      ),
    },
    {
      field: 'amount', headerName: t('quotation.columns.amount'), width: 130, align: 'right', headerAlign: 'right',
      // Lo zero è "importo non valorizzato", non "gratis": il backend usa lo
      // stesso significato quando decide se ricalcolare il totale dalle righe.
      renderCell: (params) => (
        <span className="cell-mono">{params.value ? formatCurrency(params.value as number) : '—'}</span>
      ),
    },
    {
      field: 'status', headerName: t('quotation.columns.status'), width: 150,
      // L'etichetta passa dal registro dei vocabolari e non da QUOTATION_STATUSES:
      // quella lista contiene i soli stati del prodotto, quindi uno stato
      // contribuito da un plugin non ci si trovava e finiva a video come chiave
      // grezza — "custom_status" invece del suo nome.
      // Chip del design system (`.chip` in _list.scss) invece del Chip MUI: le
      // varianti prendono i colori dal brand, quindi la lista si ricolora da
      // sola quando cambiano primary/secondary dell'azienda.
      renderCell: (params) => (
        <span className={`chip ${quotationStatusChipClass(params.value as string)}`}>
          {t(vocabularyLabelKey('quotationStatus', params.value as string))}
        </span>
      ),
    },
    // Colonne dei plugin: non ordinabili perché il sort è server-side sul core,
    // che non conosce i campi di estensione.
    ...contributors.map((c): GridColDef => ({
      field: `plugin:${c.key}`,
      headerName: c.labelKey ? t(c.labelKey) : c.key,
      width: 160,
      sortable: false,
      filterable: false,
      renderCell: (params) => <c.component entityId={params.row.quotationId} siblingIds={pageIds} />,
    })),
    {
      // Azioni a icona come nella lista contatti: l'etichetta testuale in fondo
      // a ogni riga pesava piu' del dato che accompagnava.
      field: 'actions', headerName: t('quotation.columns.actions'), sortable: false,
      width: 100, align: 'right', headerAlign: 'right',
      renderCell: (params) => (
        <span className="lc-actions">
          {import.meta.env.VITE_ENABLE_EDIT_FORMS === 'true' && (
            <Link
              to={`/quotations/${params.row.quotationId}/edit`}
              className="lc-act"
              aria-label={t('common.edit')}
            >
              <EditGlyph />
            </Link>
          )}
          <button
            type="button"
            className="lc-act del"
            aria-label={t('common.delete')}
            onClick={() => confirm.requestConfirm(
              t('quotation.deleteConfirm', { title: params.row.title }),
              () => handleDelete(params.row.quotationId),
            )}
          >
            <TrashGlyph />
          </button>
        </span>
      ),
    },
  ];
}
